import { createSlice } from '@reduxjs/toolkit'
import name from './name'

/*
 * `accountInfoStatus` is the one answer to "where is the account info fetch":
 *
 *   idle     nobody has asked yet
 *   loading  a request is out
 *   ready    it came back; `accountInfo` is authoritative, even when empty
 *   error    it failed; `accountInfoError` says how, and `accountInfo` keeps
 *            whatever the previous success brought
 *
 * It replaces three booleans that let a failure set the same two of them as
 * a success — "loaded, empty" — so a paying account whose fetch timed out
 * looked, to every consumer, like an account with no subscription. The status
 * is read off the store rather than through `useSelector` where it gates a
 * request (see `useAccountInfoRequest`): three components can ask — the paid
 * toolbar, the profile layout and PaymentProfile — and each effect otherwise
 * sees the value from its own render and all three send the same request.
 */
export const ACCOUNT_INFO_STATUS = Object.freeze({
  idle: 'idle',
  loading: 'loading',
  ready: 'ready',
  error: 'error'
})

const initialState = {
  accountInfo: {},
  accountInfoStatus: ACCOUNT_INFO_STATUS.idle,
  // What the last failure said, for the card and for anyone reading the
  // store in production. Cleared by the next success.
  accountInfoError: null,
  // Requests sent since the last success. The automatic retry reads it to
  // know when to stop; the user's "Try again" does not.
  accountInfoAttempts: 0,
  subscriptionChangeReceived: null,
  subscriptionChangePending: null,
  showPleaseWaitDialog: false
}

// The bus hands the listener whatever the handler threw: usually an object
// with `code`/`message`/`http`, sometimes a bare string. Keep the parts that
// tell a timeout from a 4xx/5xx and drop the rest, so the store stays
// serialisable.
const describeError = (error) => ({
  code: error?.code ?? null,
  http: error?.http ?? error?.status ?? null,
  message: typeof error === 'string' ? error : error?.message ?? null
})

export const content = createSlice({
  name,
  initialState,
  reducers: {
    setAccountInfoRequested: (state) => {
      state.accountInfoStatus = ACCOUNT_INFO_STATUS.loading
      state.accountInfoAttempts += 1
    },
    setAccountInfo: (state, { payload }) => {
      state.accountInfo = {
        ...payload.accountInfo
      }
      state.accountInfoStatus = ACCOUNT_INFO_STATUS.ready
      state.accountInfoError = null
      state.accountInfoAttempts = 0
      state.subscriptionChangePending = false
      state.subscriptionChangeReceived = false
    },
    // `accountInfo` is deliberately left alone: a refetch that fails after a
    // plan change keeps showing the plan the user was looking at, with the
    // card saying it could not refresh, rather than replacing it with nothing.
    setAccountInfoError: (state, { payload }) => {
      state.accountInfoStatus = ACCOUNT_INFO_STATUS.error
      state.accountInfoError = describeError(payload)
      state.subscriptionChangePending = false
      // `useSubscription` refetches while this is set, so a failed refetch
      // would otherwise ask again on every render, forever. The retry makes
      // up for it by always invalidating the cache.
      state.subscriptionChangeReceived = false
    },
    setSubscriptionChangePending: (state, { payload = false }) => {
      state.subscriptionChangePending = payload
    },
    setSubscriptionChangeError: (state, { payload = false }) => {
      state.subscriptionChangePending = false
    },
    setSubscriptionChangeReceived: (state, { payload }) => {
      // This will trigger a request for new account info
      state.subscriptionChangeReceived = payload
    }
  }
})

export const { setAccountInfo, setAccountInfoRequested, setAccountInfoError, setSubscriptionChangePending, setSubscriptionChangeReceived, setSubscriptionChangeError } = content.actions

export default content.reducer
